import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { SlidersHorizontal } from "@phosphor-icons/react";
import { adminApi, getErrorMessage } from "@/lib/api";
import { Card, btnPrimary } from "./ui";

const FIELDS = [
  ["pricing_tolerance_pct", "Pricing tolerance (±%)", 1, 100, 1],
  ["min_comparables", "Min comparable repairs for a reliable benchmark", 1, 100, 1],
  ["min_line_confidence", "Min extraction confidence (0–1)", 0, 1, 0.05],
  ["trend_min_sample", "Min reports before a trend alert", 1, 1000, 1],
  ["trend_growth_pct", "Trend alert growth threshold (%)", 1, 1000, 5],
];
const fmt = (iso) => new Date(iso).toLocaleString("en-AU", { timeZone: "Australia/Brisbane", dateStyle: "short", timeStyle: "short" });

export function BenchmarkSettings({ onSaved }) {
  const [s, setS] = useState(null);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => {
    adminApi.get("/admin/wi/settings").then(({ data }) => setS(data)).catch(() => {});
    adminApi.get("/admin/wi/settings/audit").then(({ data }) => setAudit(data)).catch(() => {});
  };
  useEffect(load, []);
  if (!s) return null;
  const save = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(FIELDS.map(([k]) => [k, Number(s[k])]).concat([["exclude_outliers", !!s.exclude_outliers]]));
      await adminApi.put("/admin/wi/settings", body);
      toast.success("Settings saved — benchmarks recalculated");
      load();
      onSaved?.();
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Card title="Benchmark & trend settings" icon={SlidersHorizontal} testId="wi-settings-card">
      <div className="space-y-2.5">
        {FIELDS.map(([k, label, min, max, step]) => (
          <label key={k} className="flex items-center justify-between gap-3 text-xs text-neutral-400">
            {label}
            <input type="number" min={min} max={max} step={step} value={s[k] ?? ""} data-testid={`wi-setting-${k}`}
              onChange={(e) => setS({ ...s, [k]: e.target.value })}
              className="w-20 bg-black/50 border border-neutral-800 rounded-lg px-2 py-1 text-sm text-white text-right tabular-nums outline-none focus:border-[#73bf44]" />
          </label>
        ))}
        <label className="flex items-center justify-between gap-3 text-xs text-neutral-400">
          Exclude exceptional (outlier) repairs from benchmarks
          <input type="checkbox" checked={!!s.exclude_outliers} onChange={(e) => setS({ ...s, exclude_outliers: e.target.checked })} className="accent-[#73bf44]" data-testid="wi-setting-exclude_outliers" />
        </label>
      </div>
      <button className={`${btnPrimary} mt-4`} onClick={save} disabled={busy} data-testid="wi-settings-save-btn">Save & recalculate</button>
      {audit.length > 0 && (
        <div className="mt-4 border-t border-neutral-800 pt-3">
          <div className="text-[0.6rem] uppercase tracking-[0.16em] font-bold text-neutral-500 mb-1.5">Change history</div>
          <ul className="space-y-1 max-h-32 overflow-y-auto text-[0.7rem] text-neutral-500" data-testid="wi-settings-audit">
            {audit.map((a) => <li key={a.id}><span className="text-neutral-600">{fmt(a.at)}</span> · {a.key}: {String(a.old)} → <span className="text-neutral-300">{String(a.new)}</span></li>)}
          </ul>
        </div>
      )}
    </Card>
  );
}
